import { useEffect, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { cacheRead } from '../api/client';

/**
 * Hook genérico de carga. Si se pasa `cacheKey` (la ruta del endpoint), funciona en modo
 * stale-while-revalidate: muestra al instante lo último cacheado y revalida por detrás,
 * actualizando solo si cambió. Sin cacheKey, es carga normal (loading hasta que llega la red).
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[], cacheKey?: string) {
  const seed = cacheKey ? cacheRead<T>(cacheKey) : null;
  const [data, setData] = useState<T | null>(seed);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(seed === null);

  useEffect(() => {
    let alive = true;
    const cached = cacheKey ? cacheRead<T>(cacheKey) : null;
    if (cached !== null) setData(cached); // instantáneo
    else setLoading(true);
    setError(null);
    loader()
      .then((d) => alive && setData(d))
      .catch((e) => alive && cached === null && setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, deps);

  return { data, error, loading };
}

const Center = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`;

// Renderiza children solo cuando hay datos; muestra carga/error mientras tanto.
export function Async<T>({
  state,
  children,
}: {
  state: { data: T | null; error: string | null; loading: boolean };
  children: (data: T) => ReactNode;
}) {
  if (state.loading && !state.data) return <Center>Cargando…</Center>;
  if (state.error && !state.data) return <Center>⚠️ {state.error}</Center>;
  if (!state.data) return <Center>Sin datos</Center>;
  return <>{children(state.data)}</>;
}
